import React from "react";

import {
  BUCKET_EXPIRED,
  type BucketCellRef,
  bucketTone,
  type DashboardSummary,
  type DirectionShelfLife,
  directionTitle,
  type OrganizationShelfLife,
  type ShelfLifeBucket,
} from "@/api/warehouse_dashboard";
import { fmtPercent, ruNum, sharePct } from "@/lib/format";
import { cn } from "@/lib/utils";
import { bucketBarWidths, hiddenLine, shelfLifeBlocks } from "@/lib/warehouse-shelf-life";

import { BucketItemsDialog } from "./BucketItemsDialog";
import { DashboardWarnings } from "./DashboardWarnings";

/** Открытая ячейка ОСГ: адрес для запроса + заголовок из сводки. */
interface OpenCell {
  cell: BucketCellRef;
  title: string;
}

/**
 * Вкладка «Сроки годности»: отдельный блок на каждую пару «юрлицо × склад
 * направления» (заголовок «ОМЕГА ФАРМ ООО · МП», мелко — имя склада), в
 * блоке корзины ОСГ строками и одна колонка «шт / доля / полоска», «Всего»
 * внизу. Блоки сеткой: 3 в ряд на `xl`, 2 на `md`, 1 на телефоне.
 *
 * Фронт рисует ответ как есть: порядок блоков и корзин — из ответа
 * (`shelfLifeBlocks` только разворачивает дерево), корзины с нулём не прячет,
 * цвет — по ключу корзины (`bucketTone`). Скрытые настройкой юрлица —
 * строкой «Скрыто настройкой: …» под блоками (`hiddenLine`), не молча.
 */
export function ShelfLifeTab({ data }: { data: DashboardSummary }): React.ReactElement {
  // Ячейка ОСГ, чьи позиции открыты в модалке.
  const [openCell, setOpenCell] = React.useState<OpenCell | null>(null);
  const { buckets, organizations, hidden } = data.shelf_life;
  const blocks = shelfLifeBlocks(organizations);
  const hiddenText = hiddenLine(hidden);

  return (
    <div className="space-y-2">
      <DashboardWarnings warnings={data.warnings} />
      {blocks.length === 0 && !hiddenText ? (
        <p className="text-[13px] text-muted-foreground">Нет остатка на складах МП/ОПТ</p>
      ) : (
        <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
          {blocks.map(({ org, direction }) => (
            <ShelfLifeBlockCard
              key={`${org.ref}:${direction.warehouse_ref}`}
              org={org}
              direction={direction}
              buckets={buckets}
              onOpen={setOpenCell}
            />
          ))}
        </div>
      )}
      {hiddenText && <p className="text-xs text-muted-foreground">{hiddenText}</p>}
      {openCell && (
        <BucketItemsDialog
          cell={openCell.cell}
          title={openCell.title}
          onClose={() => setOpenCell(null)}
        />
      )}
    </div>
  );
}

/** Один блок: юрлицо × склад направления, корзины строками. */
function ShelfLifeBlockCard({
  org,
  direction: d,
  buckets,
  onOpen,
}: {
  org: OrganizationShelfLife;
  direction: DirectionShelfLife;
  buckets: ShelfLifeBucket[];
  onOpen: (cell: OpenCell) => void;
}): React.ReactElement {
  const widths = bucketBarWidths(d, buckets);
  const heading = `${org.name} · ${directionTitle(d.direction)}`;
  return (
    <section
      aria-label={heading}
      className="min-w-0 overflow-hidden rounded-lg border bg-white"
    >
      <h2 className="border-b bg-slate-50 px-3 py-1.5 text-[13px] leading-5" title={heading}>
        <span className="block truncate font-semibold">{heading}</span>
        <span className="block truncate text-[11px] leading-4 text-muted-foreground">
          {d.warehouse_name}
        </span>
      </h2>
      <table className="w-full table-fixed text-[13px] leading-5">
        <colgroup>
          <col className="w-[148px]" />
          <col />
        </colgroup>
        <tbody>
          {/* Корзины с нулём НЕ прячем: распределение читается целиком, а
              пропущенная строка выглядит как отсутствие такого срока. */}
          {buckets.map((b) => (
            <tr key={b.key} className="border-b last:border-b-0">
              <td className="whitespace-nowrap px-2 py-1 pl-3">
                <span className="flex items-center gap-1.5">
                  <span className={cn("size-2 shrink-0 rounded-full", bucketTone(b.key).dot)} />
                  <span className={cn(b.key === BUCKET_EXPIRED && "text-rose-700")}>
                    {b.title}
                  </span>
                </span>
              </td>
              <BucketCell
                direction={d}
                bucket={b}
                barPct={widths[b.key] ?? 0}
                onOpen={() =>
                  onOpen({
                    // Ref организации бывает пустым («(без организации)») —
                    // отдаём как есть, бэкенд адресует ячейку ровно им.
                    cell: {
                      organization_ref: org.ref,
                      warehouse_ref: d.warehouse_ref,
                      bucket: b.key,
                    },
                    title: `${org.name} · ${d.warehouse_name} · ${b.title}`,
                  })
                }
              />
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t bg-slate-50/80 font-semibold">
          <tr>
            <td className="px-2 py-1 pl-3">Всего</td>
            <td className="px-2 py-1 pr-3">
              {/* Та же сетка, что у ячеек: число — под числами корзин. */}
              <span className="flex items-center gap-2">
                <span className="min-w-0 flex-1" />
                <span className="w-[64px] shrink-0 text-right tabular-nums">
                  {ruNum.format(d.total)}
                </span>
                <span className="w-[44px] shrink-0" />
              </span>
            </td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

function BucketCell({
  direction: d,
  bucket,
  barPct,
  onOpen,
}: {
  direction: DirectionShelfLife;
  bucket: ShelfLifeBucket;
  /** Ширина полоски — от самой большой корзины склада (`bucketBarWidths`). */
  barPct: number;
  onOpen: () => void;
}): React.ReactElement {
  const qty = d.buckets[bucket.key] ?? 0;
  const pct = sharePct(qty, d.total);
  // Полоска — слева от числа, в одну строку: колонка читается как гистограмма
  // сверху вниз. Ширина ОТНОСИТЕЛЬНАЯ (самая большая корзина — вся ширина), а
  // процент рядом — доля от «Всего» склада. У нуля полоски и процента нет —
  // сама строка остаётся (корзины с нулём не прячем), шум — нет.
  const body = (
    <span className="flex items-center gap-2">
      <span className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
        {qty > 0 && (
          <span
            className={cn("block h-full rounded-full", bucketTone(bucket.key).bar)}
            style={{ width: `${barPct}%` }}
          />
        )}
      </span>
      <span
        className={cn(
          "w-[64px] shrink-0 text-right tabular-nums",
          qty ? "font-medium" : "text-muted-foreground",
          qty > 0 && bucket.key === BUCKET_EXPIRED && "text-rose-700",
        )}
      >
        {ruNum.format(qty)}
      </span>
      <span className="w-[44px] shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
        {qty > 0 ? fmtPercent(pct) : ""}
      </span>
    </span>
  );
  // Кнопка — только у непустой ячейки: у нуля позиций нет, и модалка
  // «Позиций нет» по тапу была бы лишним походом в 1С.
  return (
    <td className="p-0">
      {qty > 0 ? (
        <button
          type="button"
          onClick={onOpen}
          title={`${d.warehouse_name} · ${bucket.title}: позиции`}
          className="block w-full px-2 py-1 pr-3 text-left hover:bg-sky-50 active:bg-sky-100"
        >
          {body}
        </button>
      ) : (
        <div className="px-2 py-1 pr-3">{body}</div>
      )}
    </td>
  );
}
